import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/root';
import { setPulse } from '../../redux/slices/generalSlice';

const ReferenceDropdown = ({ errors, record, onChange, onCompoundChange, codeNotes }) => {
  const [open, setOpen] = useState<boolean>(false);
  const pulse = useSelector((s: RootState) => s.general.pulse);
  const pulseRef = useRef(0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (pulse !== pulseRef.current) {
      setOpen(false);
    }
  }, [pulse]);

  const makePulse = () => {
    const now = new Date().getTime();
    pulseRef.current = now;
    dispatch(setPulse(now));
  };

  const itemClicked = (_e, item) => {
    onCompoundChange('comment', item.comments, 'reference', item.name, record.index);
    setOpen(false);
    if (pulse !== pulseRef.current) {
      makePulse();
    }
  };

  const toggleOpen = () => {
    setOpen(prev => !prev);
    makePulse();
  };

  const hasError = errors?.reference !== undefined;

  return (
    <Field>
      <InputRow $error={hasError}>
        <Input
          type="text"
          name="reference"
          autoComplete="off"
          placeholder="Select code…"
          value={record.reference || ''}
          onChange={e => onChange(e, record.index)}
        />
        <ChevronButton type="button" onClick={toggleOpen} aria-label="Toggle reference list">
          {open ? <KeyboardArrowUpIcon style={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
        </ChevronButton>
      </InputRow>
      {open && codeNotes?.length > 0 && (
        <List onMouseLeave={() => setOpen(false)}>
          {codeNotes.map((item, index) => (
            <ListItem key={index} type="button" onClick={e => itemClicked(e, item)}>
              <ItemName>{item.name}</ItemName>
              {item.comments && <ItemPreview>{item.comments}</ItemPreview>}
            </ListItem>
          ))}
        </List>
      )}
      {open && (!codeNotes || codeNotes.length === 0) && (
        <List>
          <EmptyState>No reference codes available.</EmptyState>
        </List>
      )}
    </Field>
  );
};

export default ReferenceDropdown;

const Field = styled.div`
  position: relative;
  width: 100%;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const InputRow = styled.div<{ $error: boolean }>`
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 40px;
  border: 1px solid ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  overflow: hidden;

  &:hover {
    border-color: ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--neutral-400)')};
  }

  &:focus-within {
    border-color: ${({ $error }) => ($error ? 'var(--danger-500, #d44b3f)' : 'var(--primary-600)')};
    box-shadow: var(--shadow-focus);
  }
`;

const Input = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  padding: 0 4px 0 12px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--mainText);
  min-width: 0;

  &::placeholder {
    color: var(--neutral-400);
  }
`;

const ChevronButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 100%;
  border: 0;
  border-left: 1px solid var(--border);
  background: var(--neutral-50);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  padding: 0;
  flex: 0 0 auto;

  &:hover {
    background: var(--neutral-100);
    color: var(--primary-600);
  }

  &:focus-visible {
    outline: none;
    background: var(--neutral-100);
    color: var(--primary-600);
  }
`;

const List = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: var(--popperZIndex, 5);
  max-height: 240px;
  overflow-y: auto;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow-md, 0 6px 18px rgba(0, 0, 0, 0.08));
  padding: 4px;
`;

const ListItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.12s ease;

  &:hover,
  &:focus-visible {
    background: var(--primary-50);
    outline: none;
  }
`;

const ItemName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  line-height: 1.3;
`;

const ItemPreview = styled.span`
  font-size: 0.75rem;
  color: var(--neutral-500);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EmptyState = styled.div`
  padding: 12px;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;
